import { Button } from '@/components/atoms/NewPcButton';
import { useState } from 'react';

import { CPLAT_API_ROUTES, orderQueries, usePost } from '@/api';
import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/PcTooltip';
import useCheckMembership from '@/hooks/useCheckMembership';
import { useLog } from '@/hooks/V3/useLog';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { deviceUuidState } from '@/store/atom';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes';
import { useRecoilValue } from 'recoil';
import { useOrderDetails } from '../../OrderDetailsProvider';
import { DeclineCancelModal } from './DeclineCancelModal';
import { useQueryClient } from '@tanstack/react-query';

const ConfirmCancelDialog = ({
  open,
  onOpenChange,
  onConfirm,
  failOrders,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isProcessing: boolean;
  failOrders: string[];
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent noCloseBtn className="flex w-[320px] flex-col gap-0 p-[30px]">
      {failOrders.length === 0 ? (
        <>
          <div className="text-gr-900 Title3S18">취소 승인을 진행할게요</div>
          <div className="mt-[4px] text-gr-700 Body5M14">
            구매자에게 취소 요청 완료 상태로 보여지며,
            <br /> 결제 방식에 따라 환불이 필요할 수 있어요
          </div>
          <div className="mt-[20px] flex w-full  gap-[10px]">
            <Button variant="secondary" className="h-[44px] w-full flex-1" onClick={onConfirm}>
              닫기
            </Button>
            <Button variant="solid" className="w-full flex-1" onClick={onConfirm}>
              승인하기
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="text-gr-900 Title3S18">구매자가 취소 요청을 철회했어요</div>
          <div className="mt-[4px] text-gr-700 Body5M14">
            해당 상품이 발송 대기로 변경됐어요.
            <br /> 발송 처리 탭에서 발송을 완료할 수 있어요.
          </div>

          <Button
            variant="solid"
            className="mt-[20px] h-[44px] w-full"
            onClick={() => onOpenChange(false)}
          >
            확인
          </Button>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const Controller = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [failOrders, setFailOrders] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [declineModal, setDeclineModal] = useState(false);
  const [step, setStep] = useState(1); // 1: 안내, 2: 발송정보, 3: 에러
  const [shippingCompanyCode, setShippingCompanyCode] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const isMember = useCheckMembership();

  const queryClient = useQueryClient();

  const user = useAppSelector((state) => state.user);

  const { setUserEventHistory } = useLog(user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);

  const { checkedOrders, refetchAllMarkets, shippingCompanyData, setCheckedOrders } =
    useOrderDetails();
  const { showToast } = useToast();

  // API 훅
  const { mutateAsync: confirmCancellation } = usePost(
    CPLAT_API_ROUTES.confirmCancellation,
    undefined,
    undefined,
    true,
  );

  const { mutateAsync: declineCancellation } = usePost(
    CPLAT_API_ROUTES.declineCancellation,
    undefined,
    undefined,
    true,
  );

  // 택배사 리스트 변환
  const shippingCompanyList =
    shippingCompanyData?.map((item: any) => ({
      value: item.ShippingCompanyCode,
      label: item.ShippingCompanyName,
    })) ?? [];

  // 병렬 취소 승인 처리
  const handleBulkConfirmCancellation = async () => {
    setIsProcessing(true);
    try {
      // StoreId, OrderNo가 모두 있는 주문만 필터링
      const validOrders = checkedOrders.filter((order) => order.storeId && order.orderNo);

      // 유효한 주문이 하나도 없으면: 다이얼로그 닫고, 마켓 refetch만 실행
      if (validOrders.length === 0) {
        setDialogOpen(false);
        setIsProcessing(false);
        return;
      }

      // 로그 남기기
      setUserEventHistory({
        eventName: 'cancel_confirm_execute_web_dashboard',
        eventValue: JSON.stringify({
          orderCount: validOrders.length,
          orderNos: validOrders.map((o) => o.orderNo),
        }),
        deviceUuid,
      });

      // 병렬 요청
      const results = await Promise.all(
        validOrders.map((order) =>
          confirmCancellation({
            cplatToken: user.CplatToken,
            marketId: order.marketId,
            storeId: order.storeId,
            orderNo: order.orderNo,
            orderItemList: [order.orderItemNo],
            claimNo: order.claimNo,
            cancelCount: order.cancelCount,
          }).then((res) => ({
            orderNo: order.orderNo,
            code: res?.data?.code,
          })),
        ),
      );
      // 실패 주문번호 추출
      const failed = results.filter((r) => r.code !== '200').map((r) => r.orderNo);
      setFailOrders(failed);

      if (failed.length === 0) {
        showToast('success', '취소 승인처리가 완료되었어요');
        setDialogOpen(false);
        refetchAllMarkets?.();
        setCheckedOrders([]); // ✅ 처리 후 선택 주문 비우기
        queryClient.invalidateQueries({ queryKey: orderQueries.all() });
      }
      // 실패가 있으면 모달 내용이 자동으로 바뀜
    } catch (err) {
      setFailOrders(['알 수 없는 오류']);
      setCheckedOrders([]); // 실패 시 선택 주문 비우기
    }
    setIsProcessing(false);
  };

  // 발송처리
  const handleDeclineSubmit = async () => {
    setIsProcessing(true);
    setErrorMsg(null);
    try {
      const order = checkedOrders[0]; // 한 건만 가능

      // 로그 남기기
      setUserEventHistory({
        eventName: 'cancel_decline_execute_web_dashboard',
        eventValue: order.orderNo,
        deviceUuid,
      });

      const params = {
        cplatToken: user.CplatToken,
        marketId: order.marketId,
        storeId: order.storeId,
        orderNo: order.orderNo,
        orderItemList: [order.orderItemNo],
        coupangShipmentBoxNo: order.coupangShipmentBoxNo,
        invoiceNo,
        shippingCompanyCode,
        claimNo: order.claimNo,
        ProductNo: order.productNo,
        Quantity: order.orderCount,
      };
      const res = await declineCancellation(params);
      const code = res?.data?.code;
      if (code === '200') {
        showToast('success', '발송 처리가 완료되었어요');
        setDeclineModal(false);
        setStep(1);
        setShippingCompanyCode('');
        setInvoiceNo('');
        refetchAllMarkets?.();
        setCheckedOrders([]); // ✅ 처리 후 선택 주문 비우기
      } else {
        setStep(3); // 유효하지 않은 운송장 번호
        setCheckedOrders([]); // 실패 시 선택 주문 비우기
      }
    } catch (err) {
      setStep(3);
      setCheckedOrders([]); // 실패 시 선택 주문 비우기
    }
    setIsProcessing(false);
  };

  // 거절 모달 닫기 핸들러
  const handleDeclineDialogClose = (open: boolean) => {
    setDeclineModal(open);
    if (!open) {
      setShippingCompanyCode('');
      setInvoiceNo('');
      setErrorMsg(null);
      setTimeout(() => {
        setStep(1);
      }, 10);
      setCheckedOrders([]); // 모달 닫힐 때 선택 주문 비우기
    }
  };
  const navigate = useNavigate();

  const isDeclineDisabled = checkedOrders.length > 1;
  const handleNavigateToMembership = () => {
    navigate(ROUTES.moreMembership);
  };

  return (
    <div className="sticky right-0 flex h-full items-center gap-[10px] bg-white/10 pl-[20px] backdrop-blur-[5px]">
      {isMember ? (
        <>
          <Button
            size={'small'}
            variant={'line'}
            className="h-[38px] w-[85px] Body5M14"
            onClick={() => setDialogOpen(true)}
            disabled={checkedOrders.length === 0}
          >
            취소 확인
          </Button>
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span style={{ display: 'inline-block' }}>
                  <Button
                    variant="solid"
                    size="small"
                    disabled={isDeclineDisabled || checkedOrders.length === 0}
                    className="h-[38px] w-[85px] Body5M14"
                    onClick={() => setDeclineModal(true)}
                    tabIndex={isDeclineDisabled ? -1 : 0} // disabled일 때 tab 이동 방지
                  >
                    취소 거절
                  </Button>
                </span>
              </TooltipTrigger>
              {isDeclineDisabled && (
                <TooltipContent side="top">취소거절은 한 번에 한 개씩 가능합니다</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </>
      ) : (
        <Button
          onClick={handleNavigateToMembership}
          className="h-[38px] px-[18px] text-[14px] font-bold leading-[22px]"
        >
          멤버십 가입하고 취소처리하기
        </Button>
      )}

      <ConfirmCancelDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setFailOrders([]); // 모달 닫힐 때 실패 주문 초기화
        }}
        onConfirm={handleBulkConfirmCancellation}
        isProcessing={isProcessing}
        failOrders={failOrders}
      />
      <DeclineCancelModal
        open={declineModal}
        onOpenChange={handleDeclineDialogClose}
        step={step}
        onNext={() => setStep(2)}
        onSubmit={handleDeclineSubmit}
        isProcessing={isProcessing}
        errorMsg={errorMsg}
        shippingCompanyList={shippingCompanyList}
        shippingCompanyCode={shippingCompanyCode}
        setShippingCompanyCode={setShippingCompanyCode}
        invoiceNo={invoiceNo}
        setInvoiceNo={setInvoiceNo}
      />
    </div>
  );
};

export default Controller;
